import { FC } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { SalaryDeviationFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  ReportSearchInput,
  SearchContextProvider,
  StyledButton,
  TitleWithMonthRangeNavigator,
  WidgetArea,
} from "components/common";
import { useSalaryPermissions } from "components/salary";
import { useEmployeeActions } from "components/employee";
import { Grid } from "@mui/material";
import useWintSalaryDeviationReportFilterDefinition from "./useDeviationReportFilterDefinition";
import DeviationReportInnerView from "./_partials/deviationReportInnerView";
import useWintDeviationCreateDialog from "./useWintDeviationCreateDialog";
import DeviationVariationChart from "./widgets/deviationVariationChart";
import DeviationDistributionChart from "./widgets/deviationDistributionChart";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { useWintSalaryDeviationService } from "serviceHooks";
import { useSalaryMonthPickerSelections } from "./useSalaryMonthPickerSelections";

const WintDeviationReport: FC = (props) => {
  const { isWintSalaryDeviationsAvailable, isSalaryAdmin, hasChartPermission } = useSalaryPermissions();
  const { filterFormFileds, filterQueryStateHandler, apiToReportFilterFormatter, onClickClearAll } =
    useWintSalaryDeviationReportFilterDefinition();
  const { reportToApiFilter } = useWintSalaryDeviationService();
  const { employeeSettingsMoreAction, addEmployeeMoreAction } = useEmployeeActions();
  const dispatch = useDispatch();
  const createWintDeviationDialog = useWintDeviationCreateDialog();
  const monthPickerSelections = useSalaryMonthPickerSelections();

  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Deviations);
  const today = DateHelper.today();

  const initialFilter: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter = {
    yearMonthRange: {
      start: DateHelper.getFirstDayOfMonth(today),
      end: DateHelper.getLastDayOfMonth(today),
    },
  };

  const getTopButtons = (reload: () => void) => {
    let buttons: (React.ReactElement | typeof ACTION_SEPARATOR)[] = [
      <ReportSearchInput
        placeholder={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.SearchEmployee)}
      />,
    ];

    buttons = buttons.concat([
      ACTION_SEPARATOR,
      <StyledButton size="sm" onClick={onCreateDeviationClick.bind(null, reload)}>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AddDeviation)}
      </StyledButton>,
    ]);

    buttons = buttons.concat([ACTION_SEPARATOR, <FilterButton />]);

    return buttons;
  };

  const onCreateDeviationClick = (onSubmitSuccess?: () => void) => {
    dispatch(displayDialog(createWintDeviationDialog(undefined, undefined, undefined, onSubmitSuccess)));
  };

  const topRightButtons = (reload: () => void) => (
    <DashboardButtonGroup
      buttons={getTopButtons(reload)}
      moreActions={isSalaryAdmin ? [addEmployeeMoreAction, employeeSettingsMoreAction] : []}
    />
  );

  return (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <SearchContextProvider>
          <PageLayouts.DashboardLayout<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>
            title={
              <TitleWithMonthRangeNavigator<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>
                title={title}
                monthPickerSelections={monthPickerSelections}
              />
            }
            entityKey={0}
            hasPermissions={isWintSalaryDeviationsAvailable}
            filterFields={filterFormFileds}
            filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.WintSalaryDeviation}
            filterQueryStateHandler={filterQueryStateHandler}
            reportToAPIFilterFormatter={reportToApiFilter}
            apiToReportFilterFormatter={apiToReportFilterFormatter}
            initialFilter={initialFilter}
            topRightButtons={topRightButtons(reload)}
            onClickClearAll={onClickClearAll}
          >
            {hasChartPermission && (
              <WidgetArea>
                <DeviationVariationChart />
                <DeviationDistributionChart />
              </WidgetArea>
            )}
            <Grid item container>
              <DeviationReportInnerView reloadKey={reloadKey} reloadHandler={reload} />
            </Grid>
          </PageLayouts.DashboardLayout>
        </SearchContextProvider>
      )}
    />
  );
};

export default WintDeviationReport;
